import { useEffect, useState } from "react";
import { Typography, Box, Stack, tableBodyClasses } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableSortLabel, { tableSortLabelClasses } from "@mui/material/TableSortLabel";
import TableContainer, { tableContainerClasses } from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow, { tableRowClasses } from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MzDataGrid from "@markaaz/ui/MzDataGrid";
import MzPagination from "@markaaz/ui/MzPagination";
import MzButton from "@markaaz/ui/MzButton";
import { NotFoundImg } from "assets/icons";

const sx = {
  root: {
    [`.${tableContainerClasses.root}`]: {
      boxShadow: "none",
      color: "darkBlue.c100",
      borderRadius: "8px",
      maxWidth: "980px"
    },
    [`.${tableBodyClasses.root} > .${tableRowClasses.root}:last-child .${tableCellClasses.root}`]: {
      borderBottomColor: "transparent"
    },
    [`.${tableCellClasses.root}`]: {
      borderBottomColor: "blue.c20"
    },
    [`.${tableCellClasses.head}`]: {
      backgroundColor: "blue.c20",
      color: "blue.c100"
    },
    [`.${tableSortLabelClasses.root}`]: {
      [`&:hover`]: {
        color: "unset"
      },
      [`&.${tableSortLabelClasses.active}`]: {
        color: "unset"
      },
      [`&.Mui-active .${tableSortLabelClasses.icon}`]: {
        color: "blue.c100"
      }
    }
  },

  columns: (theme) => {
    return {
      ...theme.typography.body2M,
      color: "blue.c100"
    };
  },
  dataGrid: {
    maxWidth: "1020px",
    "& .MuiDataGrid-virtualScroller": {
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "10px",
        height: "10px",
        backgroundColor: (theme) => `${theme.palette.common.white}`,
        borderRadius: "0px 0px 8px 8px"
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: (theme) => `${theme.palette.darkBlue.c20}`,
        borderRadius: "8px"
      }
    }
  },
  noResults: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexWrap: "wrap",
    alignContent: "flex-end",
    padding: "40px",
    marginLeft: "200px"
  }
};

const columns = [
  {
    id: "fileName",
    title: "File Name"
  },
  {
    id: "date",
    title: "Batch Completion Date"
  }
];

export default function BatchSummaryTable({
  loading,
  loadData,
  rows,
  order,
  orderBy,
  pagination,
  onExpand,
  onClearFilter,
  onRequestSort
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const hidePagination = rows?.length;

  return (
    <Box sx={sx.root}>
      <TableContainer component={Paper}>
        <Table>
          {loading && (
            <caption>
              <Stack my={2} alignItems="center">
                <CircularProgress />
              </Stack>
            </caption>
          )}
          {!loading && rows.length === 0 && <NotFound onClearFilter={onClearFilter} />}
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const direction = orderBy === col.id ? order : "asc";
                return (
                  <TableCell>
                    <TableSortLabel
                      IconComponent={KeyboardArrowDownIcon}
                      active={orderBy === col.id}
                      direction={direction}
                      onClick={createSortHandler(col.id)}
                    >
                      <Typography variant="body1B"> {col.title}</Typography>
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          {!loading && rows.length !== 0 && (
            <TableBody>
              {rows.map((row) => (
                <Row key={row.name} row={row} onExpand={onExpand} loadData={loadData} />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <Stack mt={10} alignItems="center" maxWidth={"980px"}>
        {!hidePagination ? null : (
          <MzPagination count={pagination.count} page={pagination.page} onChange={pagination.onChange} />
        )}
      </Stack>
    </Box>
  );
}

function Row({ row, onExpand, loadData }) {
  const [open, setOpen] = useState(false);

  const sx = [
    {
      [`.${tableCellClasses.root}`]: {
        borderBottomColor: "blue.c20"
      }
    }
  ];

  const sxOpen = {
    [`.${tableCellClasses.root}`]: {
      borderBottomColor: "transparent"
    }
  };

  if (open) sx.push(sxOpen);

  useEffect(() => {
    if (loadData) {
      setOpen(false);
    }
  }, [loadData]); // refactor needed fix for the demo

  function handleClick() {
    if (!open) {
      onExpand(row);
    }
    setOpen(!open);
  }

  return (
    <>
      <TableRow sx={sx}>
        <TableCell component="th" scope="row" sx={{ py: 5, borderBottomColor: open ? "transparent" : "blue.c20" }}>
          <Typography variant="body1" color="darkBlue.c100">
            <IconButton size="small" onClick={handleClick}>
              {open ? <KeyboardArrowDownIcon color="primary" /> : <KeyboardArrowRightIcon color="primary" />}
            </IconButton>
            {row.fileName}
          </Typography>
        </TableCell>
        <TableCell sx={{ borderBottomColor: open ? "transparent" : "blue.c20" }}>
          <Typography variant="body1" color="darkBlue.c100">
            {row.date}
          </Typography>
        </TableCell>
      </TableRow>
      <CollapsedRow open={open} rows={row.items} />
    </>
  );
}

const collapsedColumns = [
  {
    field: "category",
    headerName: "",
    sortable: false,
    width: 300,
    renderCell: (params) => <TableCell sx={sx.columns}>{params.value}</TableCell>
  },
  {
    field: "matchCount",
    headerName: "Matched Count",
    sortable: false,
    width: 164
  },
  {
    field: "noMatchCount",
    headerName: "No Match Count",
    sortable: false,
    width: 164
  },
  {
    field: "errorCount",
    headerName: "Error Count",
    sortable: false,
    width: 160
  },
  {
    field: "totalCount",
    headerName: "Total Count",
    sortable: false,
    width: 160
  }
];

function CollapsedRow({ open, rows }) {
  return (
    <>
      {open && (
        <TableRow>
          <TableCell sx={{ pt: 2, pb: 5 }} colSpan={5}>
            <MzDataGrid
              variant="4"
              getRowId={(row) => row?.["category"]}
              rowHeight={40}
              headerHeight={40}
              autoHeight
              sx={sx.dataGrid}
              rows={rows}
              columns={collapsedColumns}
              disableSelectionOnClick
              disableColumnMenu={true}
              hideFooter
              loading={rows.length === 0}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function NotFound({ onClearFilter }) {
  return (
    <Box sx={sx.noResults}>
      <NotFoundImg />
      <Typography variant="h4" pt={4} color="darkBlue.c100">
        No Results Found
      </Typography>
      <Typography variant="body1" p={2} color="darkBlue.c70">
        Please refine your filter inputs and try again.
      </Typography>
      <MzButton onClick={onClearFilter}>Clear Filter</MzButton>
    </Box>
  );
}
